import { Flex, Text, Link, Heading, Divider, Spinner } from '@customerio/pluma-components/react';
import plur from 'plur';
import type { SearchResult } from '@/services/auth/types';
import { isEmptyString } from '@/utils/string';
import * as dialogStyles from '../index.css';
import { CampaignPreview } from './campaign-preview';
import { SegmentPreview } from './segment-preview';
import { TemplatePreview } from './template-preview';
import { ActionPreview } from './action-preview';
import { NewsletterPreview } from './newsletter-preview';
import { GoalPreview } from './goal-preview';
import { CustomerPreview } from './customer-preview';
import { DocsPreview } from './docs-preview';
import { IntegrationPreview } from './integration-preview';
import { FormPreview } from './form-preview';
import { ObjectTypePreview } from './object-type-preview';
import type { IntegrationCatalog } from '../utils';
import { NON_PREVIEWABLE_KINDS } from './constants';

type PreviewContentProps = {
  result: SearchResult;
  integrationCatalog: IntegrationCatalog;
};

// This is intentionally an exhaustive discriminated-union renderer; the switch
// preserves SearchResult narrowing better than a dispatch table.
// fallow-ignore-next-line complexity
function PreviewContent({ result, integrationCatalog }: PreviewContentProps): React.JSX.Element | null {
  switch (result.kind) {
    case 'campaign':
      return <CampaignPreview result={result} />;
    case 'segment':
      return <SegmentPreview result={result} />;
    case 'template':
      return <TemplatePreview result={result} />;
    case 'action':
      return <ActionPreview result={result} />;
    case 'newsletter':
      return <NewsletterPreview result={result} />;
    case 'goal':
      return <GoalPreview result={result} />;
    case 'customer':
      return <CustomerPreview result={result} />;
    case 'docs':
      return <DocsPreview result={result} />;
    case 'source':
    case 'destination':
      return <IntegrationPreview result={result} catalog={integrationCatalog} />;
    case 'custom_form':
      return <FormPreview result={result} />;
    case 'object_type':
      return <ObjectTypePreview result={result} />;
    case 'navigation':
    case 'ad_audience':
    case 'reporting_webhook':
    case 'cdp_out':
    case 'data_out':
      return null;
  }
}

type DescriptionProps = { result: SearchResult };

function Description({ result }: DescriptionProps) {
  if (result.kind === 'campaign' || result.kind === 'segment' || result.kind === 'goal') {
    const value = result.other.description;
    return (
      <Text
        size="xs"
        color={isEmptyString(value) ? 'placeholder' : 'subtle'}
        className={!isEmptyString(value) ? 'fs-mask' : undefined}
      >
        {!isEmptyString(value) ? value : '—'}
      </Text>
    );
  }

  if (result.kind === 'action' && !isEmptyString(result.other.campaign_name)) {
    let campaignUrl: string | null = null;
    try {
      campaignUrl = !isEmptyString(result.other.campaign_url) ? new URL(result.other.campaign_url).pathname : null;
    } catch {
      // malformed URL — fall through to plain text
    }

    return (
      <Text size="xs" color="subtle">
        from Campaign{' '}
        {campaignUrl != null ? (
          <Link href={campaignUrl} className="fs-mask">
            {result.other.campaign_name}
          </Link>
        ) : (
          <Text as="span" size="xs" className="fs-mask">
            {result.other.campaign_name}
          </Text>
        )}
      </Text>
    );
  }

  return null;
}

type QuickLookPanelProps = {
  isLoading?: boolean;
  result: SearchResult | null;
  integrationCatalog: IntegrationCatalog;
};

function getPreviewTitle(result: SearchResult): string {
  if (result.kind === 'object_type') {
    if (result.other.object_count === 0) return result.title;

    const objectTypeName = plur(result.other.singular_name, result.title, result.other.object_count);
    return `${result.other.object_count} ${objectTypeName}`;
  }

  return result.title;
}

export function QuickLookPanel({ isLoading = false, result, integrationCatalog }: QuickLookPanelProps) {
  // Only show the loading spinner when we have no result yet — if a result is already
  // visible, keep showing it rather than flashing a spinner during background fetches
  // (e.g. navigation's 100ms delay on new queries, or eager-source refetches).
  if (isLoading && result == null) {
    return (
      <Flex direction="column" w="full" className={dialogStyles.quickLookPanelShell}>
        <Flex direction="column" alignItems="center" justifyContent="center" flex={1} w="full" p="200">
          <Spinner className={dialogStyles.spinner} color="information" />
        </Flex>
      </Flex>
    );
  }
  if (result == null || NON_PREVIEWABLE_KINDS.has(result.kind)) {
    return (
      <Flex direction="column" w="full" className={dialogStyles.quickLookPanelShell}>
        <Flex direction="column" alignItems="center" justifyContent="center" flex={1} w="full" p="200">
          <Text size="xs" color="subtle">
            No preview
          </Text>
        </Flex>
      </Flex>
    );
  }

  return (
    <Flex className={dialogStyles.quickLookPanelShell} overflow="hidden">
      <Flex direction="column" gap="200" w="full" flex={1} minH="0" overflow="auto" p="200">
        <Flex direction="column" gap="050">
          <Heading level="2" className="fs-mask">
            {getPreviewTitle(result)}
          </Heading>
          <Description result={result} />
        </Flex>

        <Divider />

        <Flex direction="column" gap="100" w="full" alignItems="stretch">
          <PreviewContent result={result} integrationCatalog={integrationCatalog} />
        </Flex>
      </Flex>
    </Flex>
  );
}
